import type {
  ActivityChartRow,
  ChartGrain,
  ChartQueryOpts,
  ChartRow,
  SportKey,
} from "@/types/chart.types";
import type { Activity, ActivityType, SportType } from "@/types/data.types";
import {
  addDays,
  addMonths,
  nextInstantIso,
  startOfMonth,
  startOfWeekMonday,
} from "@/utils/date.utils";
import { startOfYear } from "date-fns";
import { localDate, localMonth, localYear } from "./format.utils.ts";

const DAYS_COUNT = 30;
const WEEKS_COUNT = 12;
const MONTHS_COUNT = 12;

function maxIso(a?: string, b?: string) {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) > new Date(b) ? a : b;
}

function minIso(a?: string, b?: string) {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) < new Date(b) ? a : b;
}

export function resolveChartTimeWindow(params: {
  grain: ChartGrain;
  from?: string;
  to?: string;
}) {
  const { grain, to, from } = params;

  // Anchor the grain window to the user's selected "to" (if any)
  const anchorTo = to ? new Date(to) : new Date();

  // Build grain window based on anchorTo
  let windowFromIso: string | undefined;
  let windowToExclusiveIso: string | undefined;

  if (grain === "days") {
    const anchorDay = new Date(anchorTo);
    anchorDay.setHours(0, 0, 0, 0);
    const from = addDays(anchorDay, -DAYS_COUNT + 1);
    windowFromIso = from.toISOString();
    windowToExclusiveIso = addDays(anchorDay, 1).toISOString(); // next day 00:00
  } else if (grain === "weeks") {
    const thisWeek = startOfWeekMonday(anchorTo);
    const from = addDays(thisWeek, -(WEEKS_COUNT - 1) * 7);
    const to = addDays(thisWeek, 7); // exclusive
    windowFromIso = from.toISOString();
    windowToExclusiveIso = to.toISOString();
  } else if (grain === "months") {
    const thisMonth = startOfMonth(anchorTo);
    const from = addMonths(thisMonth, -(MONTHS_COUNT - 1));
    const to = addMonths(thisMonth, 1); // exclusive
    windowFromIso = from.toISOString();
    windowToExclusiveIso = to.toISOString();
  }
  // years + total: no fixed window by default (still respect opts.from/opts.to)

  // Combine (dateRangeAtom) bounds with grain window:
  // - lower bound = max(userFrom, windowFrom)
  // - upper bound = min(userToExclusive, windowToExclusive)
  const userFromIso = from;
  const userToExclusiveIso = to ? nextInstantIso(to) : undefined;

  return {
    fromIso: maxIso(userFromIso, windowFromIso),
    toExclusiveIso: minIso(userToExclusiveIso, windowToExclusiveIso),
  };
}

function sportKey(a: Pick<Activity, "sport_type" | "type">): SportKey {
  return a.sport_type ?? a.type ?? "Other";
}

export async function rowsToChartData(
  rows: ActivityChartRow[],
  opts: ChartQueryOpts,
) {
  const { metric, grain } = opts;
  const anchorTo = opts.to ? new Date(opts.to) : new Date();

  if (grain === "total") {
    let total = 0;
    for (const a of rows)
      total += Number(metric === "count" ? 1 : (a[metric] ?? 0));
    return { chartData: [], series: [], totalValue: total };
  }

  // Build bucket list so empty periods still show.
  const buckets: string[] = [];

  if (grain === "days") {
    const anchorDay = new Date(anchorTo);
    anchorDay.setHours(0, 0, 0, 0);
    const from = addDays(anchorDay, -DAYS_COUNT + 1);
    for (let i = 0; i < DAYS_COUNT; i++)
      buckets.push(localDate(addDays(from, i)));
  } else if (grain === "weeks") {
    const thisWeek = startOfWeekMonday(anchorTo);
    const from = addDays(thisWeek, -(WEEKS_COUNT - 1) * 7);
    for (let i = 0; i < WEEKS_COUNT; i++)
      buckets.push(localDate(addDays(from, i * 7)));
  } else if (grain === "months") {
    const thisMonth = startOfMonth(anchorTo);
    const from = addMonths(thisMonth, -(MONTHS_COUNT - 1));
    for (let i = 0; i < MONTHS_COUNT; i++) {
      buckets.push(localMonth(addMonths(from, i)));
    }
  } else if (grain === "years") {
    const yearSet = new Set<string>();
    for (const a of rows) {
      if (!a.start_date) continue;
      yearSet.add(String(new Date(a.start_date).getFullYear()));
    }
    buckets.push(...Array.from(yearSet).sort());
  }

  const byBucket: Record<string, Record<string, number>> = {};
  const resultSports = new Set<SportType | ActivityType | "Other">([]);

  for (const b of buckets) byBucket[b] = {};

  for (const a of rows) {
    if (!a.start_date) continue;
    const value = Number(metric === "count" ? 1 : (a[metric] ?? 0));
    if (!value) continue;

    const key = sportKey(a);
    resultSports.add(key);

    const d = new Date(a.start_date);

    let bucket: string | null = null;
    if (grain === "days") bucket = localDate(d);
    if (grain === "weeks") bucket = localDate(startOfWeekMonday(d));
    if (grain === "months") bucket = localMonth(startOfMonth(d));
    if (grain === "years") bucket = localYear(startOfYear(d));

    if (!bucket || !byBucket[bucket]) continue;
    byBucket[bucket][key] = (byBucket[bucket][key] ?? 0) + value;
  }

  const series = Array.from(resultSports).sort((a, b) => a.localeCompare(b));

  const chartData: ChartRow[] = buckets.map((b) => {
    const row: ChartRow = { period: b };
    for (const s of series) row[s] = byBucket[b]?.[s];
    return row;
  });

  return { chartData, series };
}
